import React, { useState } from "react";
import moment from "moment";
import Table from "components/Table/Table.jsx";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Button from "@material-ui/core/Button";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import { addGoalTasks } from "services/goalsDataProvider";

// Helper to get all days in a week (Sunday to Saturday)
function getWeekDays(startDate = null) {
  const start = startDate
    ? moment(startDate).startOf("week")
    : moment().startOf("week");
  return Array.from({ length: 7 }, (_, i) => start.clone().add(i, "days"));
}

function getTasksForDay(goal, day) {
  const result = [];
  if (goal.milestones && goal.milestones.length > 0) {
    goal.milestones.forEach(milestone => {
      const milestoneDate = milestone.target_date || goal.target_date;
      if (milestoneDate && moment(milestoneDate).isSame(day, "day")) {
        result.push({
          type: "milestone",
          name: milestone.name,
          completed: milestone.completed
        });
      }
      if (milestone.tasks && milestone.tasks.length > 0) {
        milestone.tasks.forEach(task => {
          const taskDate =
            task.target_date || milestone.target_date || goal.target_date;
          if (taskDate && moment(taskDate).isSame(day, "day")) {
            result.push({
              type: "task",
              id: task.id,
              milestone_id: milestone.id,
              name: task.name,
              target_date: taskDate,
              completed: task.completed
            });
          }
        });
      }
    });
  }
  return result;
}

function getAllGoalTasks(goals) {
  const tasks = [];
  (goals.results || goals).forEach(goal => {
    (goal.milestones || []).forEach(milestone => {
      (milestone.tasks || []).forEach(task => {
        const dueDate =
          task.target_date || milestone.target_date || goal.target_date;
        tasks.push({
          ...task,
          dueDate,
          goalId: goal.id,
          goalName: goal.name,
          milestoneId: milestone.id,
          milestoneName: milestone.name
        });
      });
    });
  });
  return tasks;
}

function getTodaysBoardTasks(goals) {
  const today = moment().startOf("day");

  return getAllGoalTasks(goals)
    .filter(task => {
      if (!task.dueDate) {
        return false;
      }

      const dueDay = moment(task.dueDate).startOf("day");
      const dayDiff = dueDay.diff(today, "days");
      const isDueTodayOrPast = dayDiff <= 0;

      if (!isDueTodayOrPast) {
        return false;
      }

      // Keep completed tasks only if they are due today.
      if (task.completed && dayDiff !== 0) {
        return false;
      }

      return true;
    })
    .sort((a, b) => {
      const aDate = a.dueDate ? moment(a.dueDate) : null;
      const bDate = b.dueDate ? moment(b.dueDate) : null;
      if (aDate && bDate) {
        return aDate.diff(bDate);
      }
      if (aDate) return -1;
      if (bDate) return 1;
      return (a.name || "").localeCompare(b.name || "");
    });
}

function getFirstDueDate(goals) {
  let dates = [];
  (goals.results || goals).forEach(goal => {
    if (goal.milestones) {
      goal.milestones.forEach(milestone => {
        const milestoneDate = milestone.target_date || goal.target_date;
        if (milestoneDate) dates.push(moment(milestoneDate));
        if (milestone.tasks) {
          milestone.tasks.forEach(task => {
            const taskDate =
              task.target_date || milestone.target_date || goal.target_date;
            if (taskDate) dates.push(moment(taskDate));
          });
        }
      });
    }
  });
  if (dates.length === 0) return null;
  return moment.min(dates);
}

const GoalsCalendar = ({ goals, axios, onGoalsUpdated }) => {
  const [showFirstDueWeek, setShowFirstDueWeek] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [showMoveDueDateModal, setShowMoveDueDateModal] = useState(false);
  const [selectedTaskForDateMove, setSelectedTaskForDateMove] = useState(null);
  const [selectedDueDate, setSelectedDueDate] = useState("");

  const openMoveDueDateModal = task => {
    setSelectedTaskForDateMove(task);
    setSelectedDueDate(
      moment(task.target_date || task.dueDate || moment()).format("YYYY-MM-DD")
    );
    setShowMoveDueDateModal(true);
  };

  const closeMoveDueDateModal = () => {
    setShowMoveDueDateModal(false);
    setSelectedTaskForDateMove(null);
    setSelectedDueDate("");
  };

  const saveMoveDueDate = async () => {
    if (!selectedTaskForDateMove || !selectedDueDate) {
      return;
    }
    const isValidDate = moment(selectedDueDate, "YYYY-MM-DD", true).isValid();
    if (!isValidDate) {
      alert("Please select a valid date.");
      return;
    }
    await updateTask(selectedTaskForDateMove, { target_date: selectedDueDate });
    closeMoveDueDateModal();
  };

  const updateTask = async (task, updates = {}) => {
    if (!axios || !task) {
      return;
    }

    const taskPayload = {
      id: task.id,
      milestone_id: task.milestoneId,
      name: task.name || "",
      description: task.description || "",
      target_date: task.target_date || task.dueDate || "",
      target_value: task.target_value,
      target_type: task.target_type,
      completed: !!task.completed,
      ...updates
    };

    try {
      await addGoalTasks(axios, task.goalId, task.milestoneId, taskPayload);
      if (typeof onGoalsUpdated === "function") {
        await onGoalsUpdated();
      }
    } catch (error) {
      alert("Failed to update task");
    }
  };

  const handleTaskActionChange = task => async event => {
    const action = event.target.value;
    event.target.value = "";

    if (!action) {
      return;
    }

    if (task.completed && (action === "start_work" || action === "move_due_date")) {
      return;
    }

    if (action === "start_work") {
      await updateTask(task, { completed: false });
      return;
    }

    if (action === "open_task") {
      window.open(`/my-goals/${task.goalId}/details#task-${task.id}`, "_blank");
      return;
    }

    if (action === "open_goal") {
      window.open(`/my-goals/${task.goalId}/details`, "_blank");
      return;
    }

    if (action === "move_due_date") {
      openMoveDueDateModal(task);
    }
  };

  const firstDueDate = getFirstDueDate(goals);
  const baseWeekStart =
    showFirstDueWeek && firstDueDate ? firstDueDate : moment();
  const weekStart = baseWeekStart.clone().add(weekOffset, "weeks");
  const weekDays = getWeekDays(weekStart);
  const today = moment().startOf("day");
  const weekDayLabels = weekDays.map(d => d.format("ddd, MMM D YYYY"));

  const tableData = (goals.results || goals).map((goal, index) => {
    const milestoneCount = goal.milestones ? goal.milestones.length : 0;
    const completedMilestoneCount = goal.milestones
      ? goal.milestones.filter(m => m.completed).length
      : 0;
    let taskCount = 0;
    let completedTaskCount = 0;
    if (goal.milestones) {
      goal.milestones.forEach(milestone => {
        if (milestone.tasks) {
          taskCount += milestone.tasks.length;
          completedTaskCount += milestone.tasks.filter(t => t.completed).length;
        }
      });
    }

    return [
      <div
        key={goal.id || `goal-${index}`}
        style={{
          fontWeight: "bold",
          color: "#1976d2",
          fontSize: "1.1em",
          minWidth: 180,
          maxWidth: 180,
          wordBreak: "break-word"
        }}
      >
        {goal.name}
        <br />
        <span
          style={{
            fontWeight: "normal",
            color: "#555",
            fontSize: "0.95em",
            marginLeft: 8
          }}
        >
          Milestones: {milestoneCount} / {completedMilestoneCount}
        </span>
        <br />
        <span
          style={{
            fontWeight: "normal",
            color: "#555",
            fontSize: "0.95em",
            marginLeft: 8
          }}
        >
          Tasks: {taskCount} / {completedTaskCount}
        </span>
      </div>,
      ...weekDays.map(day => {
        const isTodayColumn = day.isSame(today, "day");
        let items = getTasksForDay(goal, day);
        if (items.length === 0) items = <span>-</span>;
        else {
          items = (
            <ul
              key={day.format("YYYY-MM-DD")}
              style={{
                margin: 0,
                padding: 0,
                listStyle: "none",
                minHeight: 40,
                minWidth: 120,
                maxWidth: 120
              }}
            >
              {items.map((item, idx) => {
                const milestoneForTask =
                  item.type === "task"
                    ? goal.milestones &&
                      goal.milestones.find(m => m.id === item.milestone_id)
                    : null;
                const milestoneName =
                  item.type === "task"
                    ? (milestoneForTask && milestoneForTask.name) || ""
                    : item.name;
                let itemDate =
                  item.type === "milestone"
                    ? (goal.milestones &&
                        goal.milestones.find(m => m.name === item.name)
                          .target_date) ||
                      goal.target_date
                    : null;
                if (item.type === "task") {
                  itemDate =
                    (milestoneForTask &&
                      milestoneForTask.tasks &&
                      milestoneForTask.tasks.find(t => t.name === item.name)
                        .target_date) ||
                    (milestoneForTask && milestoneForTask.target_date) ||
                    goal.target_date;
                }

                let daysInfo = "";
                if (itemDate) {
                  const days = moment(itemDate).diff(moment(), "days");
                  daysInfo =
                    days < 0
                      ? `Overdue by ${Math.abs(days)} days`
                      : `${days} days left`;
                }

                const summary = `Goal: ${goal.name}\nMilestone: ${milestoneName}\n${daysInfo}`;
                const url = `/my-goals/${goal.id}/details`;

                return (
                  <li
                    key={idx}
                    style={{
                      color: item.completed ? "#4caf50" : "#f44336",
                      cursor: "pointer"
                    }}
                    title={summary}
                  >
                    <span onClick={() => window.open(url, "_blank")}>
                      {item.type === "milestone" ? "\u25C6 " : "\u25CF "}
                      {item.name}
                    </span>
                    {item.completed ? " ✓" : ""}
                  </li>
                );
              })}
            </ul>
          );
        }
        return (
          <div
            key={day.format("YYYY-MM-DD")}
            style={
              isTodayColumn
                ? {
                    backgroundColor: "#fff8e1",
                    borderRadius: 6,
                    padding: "4px"
                  }
                : undefined
            }
          >
            {items}
          </div>
        );
      })
    ];
  });

  const todaysBoardTasks = getTodaysBoardTasks(goals);

  return (
    <div>
      {showMoveDueDateModal ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.35)",
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 8,
              padding: 20,
              minWidth: 320,
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)"
            }}
          >
            <h4 style={{ marginTop: 0, marginBottom: 12 }}>Move Due Date</h4>
            <input
              type="date"
              value={selectedDueDate}
              onChange={e => setSelectedDueDate(e.target.value)}
              style={{ width: "100%", marginBottom: 16, padding: 8 }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button size="sm" color="default" onClick={closeMoveDueDateModal}>
                Cancel
              </Button>
              <Button size="sm" color="primary" onClick={saveMoveDueDate}>
                Save
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <Tabs
        value={activeTab}
        onChange={(event, value) => setActiveTab(value)}
        indicatorColor="primary"
        textColor="primary"
        style={{ marginBottom: 16 }}
      >
        <Tab label="Calendar" />
        <Tab label="Today's Tasks" />
      </Tabs>

      {activeTab === 0 ? (
        <React.Fragment>
          <h3>
            Goals Calendar ({showFirstDueWeek ? "First Due Week" : "Current Week"})
          </h3>
          <FormControlLabel
            control={
              <Switch
                checked={showFirstDueWeek}
                onChange={e => {
                  setShowFirstDueWeek(e.target.checked);
                  setWeekOffset(0);
                }}
                color="primary"
              />
            }
            label="Show First Due Week"
            style={{ marginBottom: 16 }}
          />
          <Table
            tableHeaderColor="info"
            tableHead={[
              <th
                key="goal"
                style={{ padding: "10px", minWidth: 180, maxWidth: 180 }}
              >
                Goal
              </th>,
              ...weekDayLabels.map((label, idx) => (
                (() => {
                  const isTodayColumn = weekDays[idx].isSame(today, "day");
                  return (
                <th
                  key={idx}
                  style={{
                    padding: "10px",
                    width: "-webkit-fill-available",
                    backgroundColor: isTodayColumn ? "#ffe082" : undefined,
                    borderRadius: isTodayColumn ? "6px" : undefined,
                    fontWeight: isTodayColumn ? 700 : 500
                  }}
                >
                  {label}
                </th>
                  );
                })()
              ))
            ]}
            tableData={tableData}
            tableProps={{ style: { width: "100%" } }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: 16,
              gap: 16
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setWeekOffset(weekOffset - 1)}
            >
              Previous Week
            </Button>
            <span style={{ fontWeight: "bold", fontSize: "1.1em" }}>
              {weekDays[0].format("MMM D YYYY")} - {" "}
              {weekDays[6].format("MMM D YYYY")}
            </span>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setWeekOffset(weekOffset + 1)}
            >
              Next Week
            </Button>
          </div>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <h3>Today's Tasks Board</h3>
          <p style={{ marginTop: 0, color: "#666" }}>
            Includes tasks due today or overdue.
          </p>
          {todaysBoardTasks.length === 0 ? (
            <p>No tasks for today yet.</p>
          ) : (
            <Table
              tableHeaderColor="info"
              tableHead={[
                "Task",
                "Goal",
                "Milestone",
                "Due",
                "Status",
                "Actions"
              ]}
              tableData={todaysBoardTasks.map(task => {
                const dueText = task.dueDate
                  ? moment(task.dueDate).format("LL")
                  : "No due date";
                const dueDay = task.dueDate
                  ? moment(task.dueDate).startOf("day")
                  : null;
                const dayDiff = dueDay
                  ? dueDay.diff(moment().startOf("day"), "days")
                  : null;

                return [
                  task.name || "Untitled task",
                  task.goalName || "Goal",
                  task.milestoneName || "Milestone",
                  <div key={`due-${task.goalId}-${task.id}`}>
                    <div>{dueText}</div>
                    {dayDiff === 0 ? (
                      <span
                        style={{
                          color: "#8a6d1d",
                          backgroundColor: "#fff8e1",
                          padding: "2px 8px",
                          borderRadius: "999px",
                          fontWeight: 600,
                          fontSize: "11px"
                        }}
                      >
                        Due today
                      </span>
                    ) : null}
                    {dayDiff !== null && dayDiff < 0 ? (
                      <span
                        style={{
                          color: "#b71c1c",
                          backgroundColor: "#ffebee",
                          padding: "2px 8px",
                          borderRadius: "999px",
                          fontWeight: 600,
                          fontSize: "11px"
                        }}
                      >
                        Overdue
                      </span>
                    ) : null}
                  </div>,
                  task.completed ? (
                    <span
                      style={{
                        color: "#2e7d32",
                        backgroundColor: "#e8f5e9",
                        padding: "4px 8px",
                        borderRadius: "999px",
                        fontWeight: 600
                      }}
                    >
                      Completed
                    </span>
                  ) : (
                    <span
                      style={{
                        color: "#c62828",
                        backgroundColor: "#ffebee",
                        padding: "4px 8px",
                        borderRadius: "999px",
                        fontWeight: 600
                      }}
                    >
                      Open
                    </span>
                  ),
                  <div
                    key={`task-actions-${task.goalId}-${task.milestoneId}-${task.id}`}
                    style={{ display: "flex", gap: 8 }}
                  >
                    <select
                      defaultValue=""
                      onChange={handleTaskActionChange(task)}
                      style={{ height: "32px", fontSize: "12px", minWidth: "140px" }}
                    >
                      <option value="" disabled>
                        Actions
                      </option>
                      {!task.completed ? (
                        <option value="start_work">Start work</option>
                      ) : null}
                      {!task.completed ? (
                        <option value="move_due_date">Move due date</option>
                      ) : null}
                      <option value="open_task">Open task</option>
                      <option value="open_goal">Open goal</option>
                    </select>
                  </div>
                ];
              })}
            />
          )}
        </React.Fragment>
      )}
    </div>
  );
};

export default GoalsCalendar;
